// src/App.jsx
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import RequireAuth from './components/RequireAuth';
import RequireAdmin from './components/RequireAdmin';
import RequireInternal from './components/RequireInternal';
import RequireRight from './components/RequireRight';
import { ChatProvider } from './contexts/ChatContext.jsx';
import ChatAgent from './components/ChatAgent.jsx';
import ChatDM from './components/ChatDM.jsx';
import Login from './views/Login';
import ForgotPassword from './views/ForgotPassword';
import Dashboard from './views/Dashboard';
import Receipts from './views/Receipts';
import Orders from './views/Orders';
import EOrders from './views/EOrders';
import Freight from './views/Freight.jsx';
import Invoices from './views/Invoices';
import InvoiceDetail from './views/InvoiceDetail';
import Clients from './views/Clients';
import Inventory from './views/Inventory';
import Users from './views/Users';
import Settings from './views/Settings';
import Payments from './views/Payments';
import InvoiceEditor from './views/InvoiceEditor';
import Receive from './views/Receive';
import Items from './views/Items';
import Reports from './views/reports/Reports.jsx';
import Warehouse from './views/Warehouse.jsx';
import WarehouseDesigner from './views/WarehouseDesigner.jsx';
import PickingReplenishment from './views/PickingReplenishment.jsx';
import { readStoredUser, isAdminUser, isClientUser } from './utils/userFlags.js';
import ShoppingIntegrations from './views/ShoppingIntegrations.jsx';
import ShippingIntegrations from './views/ShippingIntegrations.jsx';
import ShippingFunding from './views/ShippingFunding.jsx';
import PaymentProcessing from './views/PaymentProcessing.jsx';
import WarehouseConfig from './views/WarehouseConfig.jsx';
import FtzSiteConfiguration from './views/FtzSiteConfiguration.jsx';
import AppConfig from './views/AppConfig.jsx';
import ItemBundles from './views/ItemBundles.jsx';
import ShippingPackages from './views/ShippingPackages.jsx';
import ServerStatus from './views/ServerStatus.jsx';
import Suppliers from './views/Suppliers.jsx';
import Carriers from './views/Carriers.jsx';
import EditWarehouseFees from './views/EditWarehouseFees.jsx';
import EditReceiptFees from './views/EditReceiptFees.jsx';
import EditOrderFees from './views/EditOrderFees.jsx';
import EditEOrderFees from './views/EditEOrderFees.jsx';
import EditCustomFees from './views/EditCustomFees.jsx';
import InvadersView from './views/InvadersView.jsx';
import NokiaSnake from './views/NokiaSnake.jsx';
import SideBar from './components/SideBar';
import TopHeader from './components/TopHeader';
import ToastHost from './components/ToastHost.jsx';
import SettingsModal from './components/SettingsModal.jsx';
import PeriodicInvoicing from './views/PeriodicInvoicing.jsx';
import MonthlyInvoicing from './views/MonthlyInvoicing.jsx';
import WeeklyInvoicing from './views/WeeklyInvoicing.jsx';
import './App.css';
import { clearToasts } from './utils/toast';
import { resolveSidebarTextColor } from './utils/themeContrast.js';
import CompanyCalendar from './views/CompanyCalendar.jsx';
import PurchaseOrders from './views/PurchaseOrders.jsx';
import RoleConfiguration from './views/RoleConfiguration.jsx';
import {
  CALENDAR_RIGHTS,
  CLIENTS_RIGHTS,
  EORDER_RIGHTS,
  FREIGHT_RIGHTS,
  INVOICE_RIGHTS,
  INVENTORY_RIGHTS,
  ITEM_RIGHTS,
  ORDER_RIGHTS,
  PAYMENT_RIGHTS,
  PICKING_RIGHTS,
  PURCHASE_ORDERS_RIGHTS,
  RECEIPT_RIGHTS,
  REPORT_RIGHTS,
  WAREHOUSE_RIGHTS,
} from './utils/moduleRights.js';

// --- THEME PERSISTENCE LOGIC ---//
const THEME_KEYS = [
  'weak_primary_color', 'strong_primary_color', 'primary_color', 'primary_hover',
  'background_dark', 'card_background', 'input_background', 'text_color',
  'error_color', 'success_color', 'evenrow_color', 'button_text_color', 'sidebar_text_color'
];

const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

function applyTheme(themeData) {
  const defaults = {
    weak_primary_color: '#ffd0b3',
    strong_primary_color: '#ff6200',
    primary_color: '#ff6200',
    primary_hover: '#ff8c00',
    background_dark: '#1a1a1a',
    card_background: '#2c2c2c',
    input_background: '#3a3a3a',
    text_color: '#ffffff',
    error_color: '#ff0000',
    success_color: '#00ff00',
    evenrow_color: '#fdfdfd',
    selected_row_color: '#ffffff',
    button_text_color: '#ffffff',
    sidebar_text_color: ''
  };
  const theme = { ...defaults, ...themeData };
  const sidebarTextColor = resolveSidebarTextColor(theme, theme.text_color);
  document.documentElement.style.setProperty('--weak-primary-color', theme.weak_primary_color);
  document.documentElement.style.setProperty('--strong-primary-color', theme.strong_primary_color);
  document.documentElement.style.setProperty('--primary-color', theme.primary_color);
  document.documentElement.style.setProperty('--primary-hover', theme.primary_hover);
  document.documentElement.style.setProperty('--background-dark', theme.background_dark);
  document.documentElement.style.setProperty('--card-background', theme.card_background);
  document.documentElement.style.setProperty('--input-background', theme.input_background);
  document.documentElement.style.setProperty('--text-color', theme.text_color);
  document.documentElement.style.setProperty('--error-color', theme.error_color);
  document.documentElement.style.setProperty('--success-color', theme.success_color);
  document.documentElement.style.setProperty('--evenrow-color', theme.evenrow_color);
  document.documentElement.style.setProperty('--selected-row-color', theme.selected_row_color);
  document.documentElement.style.setProperty('--button-text-color', theme.button_text_color);
  document.documentElement.style.setProperty('--sidebar-text-color', sidebarTextColor);
}

// On startup, check localStorage and apply theme (run once)
(function reapplyThemeFromStorage() {
  try {
    let theme = {};
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.themeData) theme = user.themeData;
    else if (localStorage.getItem('themeData')) theme = JSON.parse(localStorage.getItem('themeData'));
    if (theme && Object.keys(theme).length > 0) {
      applyTheme(theme);
    } else {
      console.log('NO THEME');
    }
  } catch {
    applyTheme({});
  }
})();

function NotFound() {
  return (
    <div style={{ padding: '1rem', color: 'var(--text-color)' }}>
      <h2>404 - Not Found</h2>
    </div>
  );
}

// Helper: read flags from stored user
function getUserFlags() {
  const u = readStoredUser();
  return { isAdmin: isAdminUser(u), isClient: isClientUser(u) };
}

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showNav = location.pathname !== '/' && location.pathname !== '/forgot-password';
  const { isAdmin, isClient } = getUserFlags();
  const [showOrdersNav, setShowOrdersNav] = useState(true);
  const [showFreightNav, setShowFreightNav] = useState(true);
  const [showInvoicingNav, setShowInvoicingNav] = useState(true);
  const [showInvoicesNav, setShowInvoicesNav] = useState(true);
  const [showClientsNav, setShowClientsNav] = useState(true);
  const [showWarehouseFeesNav, setShowWarehouseFeesNav] = useState(true);

  // Keep previous page as background when viewing /settings so main content doesn't change
  const [bgLocation, setBgLocation] = useState(null);
  useEffect(() => {
    if (location.pathname !== '/settings') setBgLocation(location);
  }, [location]);
  const mainLocation = (location.pathname === '/settings') ? (bgLocation || { ...location, pathname: '/dashboard' }) : location;

  // Clear any lingering toasts when navigating between views
  useEffect(() => {
    clearToasts();
  }, [location.pathname]);

  // ---- Feature toggles from appconfig (show/hide Orders menu) ----
  useEffect(() => {
    if (!showNav) return;
    let cancelled = false;
    const url = API_BASE ? `${API_BASE}/api/appconfig` : '/api/appconfig';
    (async () => {
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const cfg = await res.json().catch(() => ({}));
        if (cancelled) return;
        const interpret = (val) => {
          if (val == null) return true;
          if (typeof val === 'number') return Number(val) !== 0;
          if (typeof val === 'boolean') return val;
          const text = String(val).trim().toLowerCase();
          if (!text) return true;
          return text !== '0' && text !== 'false' && text !== 'no';
        };
        setShowOrdersNav(interpret(cfg?.showorders));
        setShowFreightNav(interpret(cfg?.showfreight));
        const invoicesToggle = cfg?.showinvoices ?? cfg?.showinvoicing;
        setShowInvoicingNav(interpret(cfg?.showinvoicing));
        setShowInvoicesNav(interpret(invoicesToggle));
        setShowClientsNav(interpret(cfg?.showclients));
        setShowWarehouseFeesNav(interpret(cfg?.showwarehousefees));
      } catch {
        if (!cancelled) {
          setShowOrdersNav(true);
          setShowFreightNav(true);
          setShowInvoicingNav(true);
          setShowInvoicesNav(true);
          setShowClientsNav(true);
          setShowWarehouseFeesNav(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [showNav]);

  // ---- Idle session timeout (frontend guard) ----
  useEffect(() => {
    const lastActiveRef = { current: Date.now() };
    const timerRef = { current: null };

    const readSessionTimeoutMs = () => {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}') || {};
        const min = Number(u.sessiontimeout ?? 0);
        if (!Number.isFinite(min) || min <= 0) return 0; // 0 or invalid => no idle auto-logout
        return Math.max(60_000, min * 60 * 1000);
      } catch {
        return 0;
      }
    };

    const performLogout = () => {
      try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('currentUser');
        localStorage.removeItem('chat.agent.open');
        localStorage.removeItem('chat.dm.open');
        localStorage.removeItem('chat.dm.activeUserId');
      } catch {}
      try { window.dispatchEvent(new CustomEvent('auth:logout')); } catch {}
      window.location.assign('/');
    };

    const tick = () => {
      const ttlMs = readSessionTimeoutMs();
      if (!ttlMs) return;
      const now = Date.now();
      if (now - lastActiveRef.current >= ttlMs) {
        performLogout();
      }
    };

    const restartTimer = () => {
      try { if (timerRef.current) clearInterval(timerRef.current); } catch {}
      timerRef.current = null;
      const ttlMs = readSessionTimeoutMs();
      if (!ttlMs) return;
      const interval = Math.min(Math.max(30_000, ttlMs / 2), 120_000);
      timerRef.current = setInterval(tick, interval);
    };

    const markActive = () => {
      lastActiveRef.current = Date.now();
    };

    const events = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    events.forEach(evt => window.addEventListener(evt, markActive, { passive: true }));
    const onLogin = () => {
      lastActiveRef.current = Date.now();
      restartTimer();
    };
    const onLogout = () => {
      try { if (timerRef.current) clearInterval(timerRef.current); } catch {}
      timerRef.current = null;
    };
    window.addEventListener('auth:login', onLogin);
    window.addEventListener('auth:logout', onLogout);

    // Start timer for current session (if any)
    restartTimer();

    return () => {
      events.forEach(evt => window.removeEventListener(evt, markActive));
      window.removeEventListener('auth:login', onLogin);
      window.removeEventListener('auth:logout', onLogout);
      try { if (timerRef.current) clearInterval(timerRef.current); } catch {}
    };
  }, []);

  return (
    <div className="App" style={{ display: 'flex', flexDirection: 'row', minHeight: '100vh' }}>
      {showNav && (
        <SideBar
          isAdmin={isAdmin}
          isClient={isClient}
          showOrders={showOrdersNav}
          showFreight={showFreightNav}
          showInvoicing={showInvoicingNav}
          showClients={showClientsNav}
          showWarehouseFees={showWarehouseFeesNav}
        />
      )}
      <div className={`main-content ${showNav ? 'has-topbar' : ''}`}>
        {showNav && <TopHeader />}
        {showNav && <ToastHost />}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Routes location={mainLocation}>
            {/* Public */}
            <Route path="/" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            {/* Shared (client + internal) */}
            <Route
              path="/dashboard"
              element={(
                <RequireAuth>
                  <Dashboard
                    showOrdersEnabled={showOrdersNav}
                    showInvoicesEnabled={showInvoicesNav}
                  />
                </RequireAuth>
              )}
            />
            <Route path="/receipts" element={<RequireAuth><RequireRight anyOf={RECEIPT_RIGHTS}><Receipts /></RequireRight></RequireAuth>} />
            <Route path="/orders" element={<RequireAuth><RequireRight anyOf={ORDER_RIGHTS}><Orders /></RequireRight></RequireAuth>} />
            <Route path="/eorders" element={<RequireAuth><RequireRight anyOf={EORDER_RIGHTS}><EOrders /></RequireRight></RequireAuth>} />
            <Route path="/inventory" element={<RequireAuth><RequireRight anyOf={INVENTORY_RIGHTS}><Inventory /></RequireRight></RequireAuth>} />
            <Route path="/calendar" element={<RequireInternal><RequireRight anyOf={CALENDAR_RIGHTS}><CompanyCalendar /></RequireRight></RequireInternal>} />
            <Route path="/purchase-orders" element={<RequireAuth><RequireRight anyOf={PURCHASE_ORDERS_RIGHTS}><PurchaseOrders /></RequireRight></RequireAuth>} />
            <Route
              path="/invoices"
              element={(
                <RequireAuth>
                  <RequireRight anyOf={INVOICE_RIGHTS}>
                    <Invoices />
                  </RequireRight>
                </RequireAuth>
              )}
            />
            <Route path="/invoice/:id" element={<RequireInternal><InvoiceDetail /></RequireInternal>} />
            <Route path="/invoices/new" element={<RequireInternal><InvoiceEditor /></RequireInternal>} />
            <Route path="/invoice/:id/edit" element={<RequireInternal><InvoiceEditor /></RequireInternal>} />
            <Route path="/payments" element={<RequireInternal><RequireRight anyOf={PAYMENT_RIGHTS}><Payments /></RequireRight></RequireInternal>} />
            <Route path="/invoices-periodic" element={<RequireInternal><RequireRight anyOf={['invoices.create', 'invoices.edit']}><PeriodicInvoicing /></RequireRight></RequireInternal>} />
            <Route path="/invoices-weekly" element={<RequireInternal><RequireRight anyOf={['invoices.create', 'invoices.edit']}><WeeklyInvoicing /></RequireRight></RequireInternal>} />
            <Route path="/invoices-monthly" element={<RequireInternal><RequireRight anyOf={['invoices.create', 'invoices.edit']}><MonthlyInvoicing /></RequireRight></RequireInternal>} />
            <Route path="/warehouse" element={<RequireInternal><RequireRight anyOf={WAREHOUSE_RIGHTS}><Warehouse /></RequireRight></RequireInternal>} />
            <Route path="/picking-replenishment" element={<RequireInternal><RequireRight anyOf={PICKING_RIGHTS}><PickingReplenishment /></RequireRight></RequireInternal>} />
            <Route path="/warehouse-config" element={<RequireAdmin><WarehouseConfig /></RequireAdmin>} />
            <Route path="/ftz-sites" element={<RequireAdmin><FtzSiteConfiguration /></RequireAdmin>} />
            <Route path="/receive/:receiptid" element={<RequireInternal><RequireRight anyOf={RECEIPT_RIGHTS}><Receive /></RequireRight></RequireInternal>} />
            <Route
              path="/reports"
              element={(
                <RequireAuth>
                  <RequireRight anyOf={REPORT_RIGHTS}>
                    <Reports />
                  </RequireRight>
                </RequireAuth>
              )}
            />
            <Route path="/invaders" element={<RequireAuth><InvadersView /></RequireAuth>} />
            <Route path="/snake" element={<RequireAuth><NokiaSnake /></RequireAuth>} />
            {/* Internal-only (non-client) */}
            <Route path="/clients" element={<RequireInternal><RequireRight anyOf={CLIENTS_RIGHTS}><Clients /></RequireRight></RequireInternal>} />
            <Route path="/freight" element={<RequireInternal><RequireRight anyOf={FREIGHT_RIGHTS}><Freight /></RequireRight></RequireInternal>} />
            <Route path="/items" element={<RequireAuth><RequireRight anyOf={ITEM_RIGHTS}><Items /></RequireRight></RequireAuth>} />
            <Route path="/bundles" element={<RequireAuth><RequireRight anyOf={ITEM_RIGHTS}><ItemBundles /></RequireRight></RequireAuth>} />
            {/* Admin-only (also internal-only by definition) */}
            <Route path="/admin" element={<RequireAdmin><Navigate to="/users" replace /></RequireAdmin>} />
            <Route path="/appconfig" element={<RequireAdmin><AppConfig /></RequireAdmin>} />
            <Route path="/users" element={<RequireAdmin><Users /></RequireAdmin>} />
            <Route path="/role-configuration" element={<RequireAdmin><RoleConfiguration /></RequireAdmin>} />
            <Route path="/suppliers" element={<RequireAdmin><Suppliers /></RequireAdmin>} />
            <Route path="/carriers" element={<RequireAdmin><Carriers /></RequireAdmin>} />
            {/* Settings is now a modal overlay — keep route reserved but not rendered here */}
            <Route path="/warehouses" element={<RequireAdmin><WarehouseDesigner /></RequireAdmin>} />
            <Route path="/warehouse-designer" element={<RequireAdmin><WarehouseDesigner /></RequireAdmin>} />
            <Route path="/shoppingintegrations" element={<RequireAdmin><ShoppingIntegrations /></RequireAdmin>} />
            <Route path="/shippingintegrations" element={<RequireAdmin><ShippingIntegrations /></RequireAdmin>} />
            <Route path="/shipping-funding" element={<RequireAdmin><ShippingFunding /></RequireAdmin>} />
            <Route path="/payment-processing" element={<RequireAdmin><PaymentProcessing /></RequireAdmin>} />
            <Route path="/shipping-packages" element={<RequireAdmin><ShippingPackages /></RequireAdmin>} />
            <Route path="/server-status" element={<RequireAdmin><ServerStatus /></RequireAdmin>} />
            <Route path="/templates/warehousefees" element={<RequireAdmin><EditWarehouseFees /></RequireAdmin>} />
            <Route path="/templates/receiptfees" element={<RequireAdmin><EditReceiptFees /></RequireAdmin>} />
            <Route path="/templates/orderfees" element={<RequireAdmin><EditOrderFees /></RequireAdmin>} />
            <Route path="/templates/eorderfees" element={<RequireAdmin><EditEOrderFees /></RequireAdmin>} />
            <Route path="/templates/customfees" element={<RequireAdmin><EditCustomFees /></RequireAdmin>} />
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </div>
      {/* Modal overlays rendered globally */}
      <SettingsModal
        open={location.pathname === '/settings'}
        onClose={() => {
          if (bgLocation) navigate(-1);
          else navigate('/dashboard', { replace: true });
        }}
      />
      {showNav && !isClient && <ChatAgent />}
      {showNav && !isClient && <ChatDM />}
    </div>
  );
};

function App() {
  return (
    <ChatProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppContent />
      </Router>
    </ChatProvider>
  );
}

export default App;
